import { c as _c } from "react/compiler-runtime";
import { Button, Card, Field, HStack, Spinner, Text, Textarea, VStack } from '@chakra-ui/react';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@src/components/chakra-ui/dialog';
import { useToast } from '@src/components/chakra-ui/use-toast';
import { UploadButton } from '@src/components/sco/Account/UploadButton';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { scoBackendUrl } from '@src/lib/startups-url';
import { useState } from 'react';
import { LuFileUp, LuWandSparkles } from 'react-icons/lu';
export type ApplyQuickFillExtracted = {
  headline?: string | null;
  categories?: string[] | null;
  skills?: string[] | null;
  experiences?: Array<{
    organizationName?: string | null;
    experienceTypeKey?: string | null;
    positionDescription?: string | null;
    userExperienceId?: string | null;
  } | null> | null;
};
type QuickFillModal = 'pdf' | 'bio' | null;
const ProcessingState = t0 => {
  const $ = _c(3);
  const {
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Spinner size="lg" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <VStack align="center" gap={3} paddingY={8}>{t1}<Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm" textAlign="center">{label}</Text></VStack>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const ApplyQuickFill = ({
  currentUserId,
  extracting,
  onExtract
}: {
  currentUserId?: string | null;
  extracting: boolean;
  onExtract: (input: {
    bio?: string;
    profileStoredFileId?: string;
  }) => Promise<void>;
}) => {
  const toast = useToast();
  const [modal, setModal] = useState<QuickFillModal>(null);
  const [bio, setBio] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const busy = extracting || pending;
  const closeModal = () => {
    if (busy) {
      return;
    }
    setModal(null);
    setError(null);
  };
  const finishExtract = async (input: {
    bio?: string;
    profileStoredFileId?: string;
  }) => {
    setError(null);
    setPending(true);
    try {
      await onExtract(input);
      setModal(null);
      toast({
        description: 'Empty fields were filled. Existing answers were left as-is — review each step.',
        title: 'Application pre-filled',
        type: 'success'
      });
    } catch (err) {
      setError(prettyErrorMessage(err) || 'Could not extract an advisor profile from that input.');
    } finally {
      setPending(false);
    }
  };
  return <Card.Root variant={'subtle'}>
      <Card.Body>
        <VStack align={'stretch'} gap={4}>
          <VStack align={'stretch'} gap={1}>
            <Text fontWeight={'semibold'}>Quick-fill your application</Text>
            <Text color={'colorPalette.fg/60'} fontFamily={'longform'} fontSize={'sm'}>
              Upload a PDF or paste a bio. We&apos;ll fill empty fields — name, location, skills, experience,
              headline — and leave anything you already entered.
            </Text>
          </VStack>

          <HStack gap={3} wrap={'wrap'}>
            <Button size={'sm'} variant={'outline'} onClick={() => setModal('pdf')}>
              <LuFileUp />
              Upload a PDF
            </Button>
            <Button size={'sm'} variant={'outline'} onClick={() => setModal('bio')}>
              <LuWandSparkles />
              Detect from Bio
            </Button>
          </HStack>
        </VStack>
      </Card.Body>

      <DialogRoot closeOnEscape={!busy} closeOnInteractOutside={!busy} open={modal === 'pdf'} placement={'center'} scrollBehavior={'inside'} size={'md'} onOpenChange={event => {
      if (!event.open) {
        closeModal();
      }
    }}>
        <DialogContent bgColor={'colorPalette.container'}>
          <DialogHeader>
            <DialogTitle>Upload a PDF</DialogTitle>
            <DialogCloseTrigger disabled={busy} />
          </DialogHeader>
          <DialogBody>
            {busy ? <ProcessingState label={'Reading your PDF and filling empty application fields…'} /> : <VStack align={'stretch'} gap={4}>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                  A LinkedIn PDF export or a resume both work. We fill empty fields only — existing name,
                  location, skills, experience, and headline stay as they are.
                </Text>
                <VStack align={'stretch'} gap={2}>
                  <Text fontSize={'sm'} fontWeight={'medium'}>
                    How to export from LinkedIn
                  </Text>
                  <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                    Open your LinkedIn profile (the full profile page, not the feed). On desktop, click
                    Resources or More → Save to PDF. Upload that file here. A standard resume PDF is fine too
                    — we cannot log into LinkedIn and pull your profile.
                  </Text>
                </VStack>
                {error ? <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
                    {error}
                  </Text> : null}
              </VStack>}
          </DialogBody>
          {busy ? null : <DialogFooter>
              <Button variant={'ghost'} onClick={closeModal}>
                Cancel
              </Button>
              <UploadButton afterError={err_0 => setError(typeof err_0 === 'string' ? err_0 : 'PDF upload failed.')} allowedExtensions={['pdf']} buttonProps={{
            size: 'sm',
            variant: 'cta'
          }} maxSizeInBytes={10000000} uploadUrl={scoBackendUrl('/assets/upload-document')} afterSuccess={(_url, storedFileId) => {
            if (!storedFileId) {
              setError('Upload succeeded but no file id was returned.');
              return;
            }
            void finishExtract({
              profileStoredFileId: storedFileId
            });
          }} storedFile={{
            attachmentId: currentUserId || '',
            attachmentType: 'User',
            contextKey: 'advisor-linkedin-profile-pdf'
          }}>
                <LuFileUp />
                Choose PDF
              </UploadButton>
            </DialogFooter>}
        </DialogContent>
      </DialogRoot>

      <DialogRoot closeOnEscape={!busy} closeOnInteractOutside={!busy} open={modal === 'bio'} placement={'center'} scrollBehavior={'inside'} size={'md'} onOpenChange={event_0 => {
      if (!event_0.open) {
        closeModal();
      }
    }}>
        <DialogContent bgColor={'colorPalette.container'}>
          <DialogHeader>
            <DialogTitle>Detect from bio</DialogTitle>
            <DialogCloseTrigger disabled={busy} />
          </DialogHeader>
          <DialogBody>
            {busy ? <ProcessingState label={'Detecting details from your bio and filling empty fields…'} /> : <VStack align={'stretch'} gap={4}>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                  Paste text about you — LinkedIn About, a short bio, or a few paragraphs of work history.
                  We&apos;ll detect name, location, skills, experience, and a headline, and only fill fields
                  that are still empty.
                </Text>
                <Field.Root>
                  <Field.Label>About you</Field.Label>
                  <Textarea disabled={busy} placeholder={'Paste your LinkedIn About section, a short resume, or any bio.'} rows={8} value={bio} onChange={event_1 => setBio(event_1.target.value)} />
                </Field.Root>
                {error ? <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
                    {error}
                  </Text> : null}
              </VStack>}
          </DialogBody>
          {busy ? null : <DialogFooter>
              <Button variant={'ghost'} onClick={closeModal}>
                Cancel
              </Button>
              <Button disabled={!bio.trim()} variant={'cta'} onClick={() => void finishExtract({
            bio: bio.trim()
          })}>
                <LuWandSparkles />
                Detect from bio
              </Button>
            </DialogFooter>}
        </DialogContent>
      </DialogRoot>
    </Card.Root>;
};